import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Fire, Copy } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HeatmapDialog({ claimId, page, onClose }) {
  const [showOriginal, setShowOriginal] = useState(false);
  if (!page) return null;
  const src = showOriginal
    ? `${API}/claims/${claimId}/pages/${page.page_number}/image`
    : `${API}/fraud/document/${claimId}/heatmap/${page.page_number}`;
  return (
    <Dialog open={!!page} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="heatmap-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Fire size={18} className="text-orange-600" /> Tamper Heatmap — Page {page.page_number}
            <button onClick={() => setShowOriginal(v => !v)} className="ml-auto text-xs text-[#002FA7] hover:underline" data-testid="heatmap-toggle">
              {showOriginal ? "Show heatmap" : "Show original"}
            </button>
          </DialogTitle>
        </DialogHeader>
        <div className="relative max-h-[70vh] overflow-auto bg-gray-100 rounded">
          <img src={src} alt={`Page ${page.page_number} ${showOriginal ? "original" : "heatmap"}`} className="w-full" data-testid="heatmap-image" />
          {!showOriginal && page.ela?.heat_bbox && (
            <div className="absolute border-2 border-red-500 pointer-events-none"
                 style={{ left: `${page.ela.heat_bbox.x * 100}%`, top: `${page.ela.heat_bbox.y * 100}%`, width: `${page.ela.heat_bbox.w * 100}%`, height: `${page.ela.heat_bbox.h * 100}%` }} />
          )}
        </div>
        <p className="text-[11px] text-gray-500">
          Blue = uniform compression (consistent with original). Yellow/red = regions whose error level differs from the page — possible pasted or re-saved content.
          ELA anomalous blocks: {page.ela?.hot_blocks || 0}/{page.ela?.total_blocks || 0} · Copy-move matches: {page.copy_move?.matched_pairs || 0} · Noise outliers: {page.noise?.outlier_blocks || 0}
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default function FraudPageForensics({ pages, claimId, suspiciousPages, onSelectPage, thresholds }) {
  const cmMin = thresholds?.cm_min_matches ?? 12;
  const [heatPage, setHeatPage] = useState(null);
  if (!pages?.length) return null;
  const sus = new Set(suspiciousPages || []);

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden" data-testid="fraud-page-forensics">
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
              <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Page</th>
              <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Type</th>
              <th className="px-3 py-1.5 text-center font-semibold text-gray-500">ELA</th>
              <th className="px-3 py-1.5 text-center font-semibold text-gray-500">Copy-move</th>
              <th className="px-3 py-1.5 text-center font-semibold text-gray-500">Noise</th>
              <th className="px-3 py-1.5 text-center font-semibold text-gray-500">Edges</th>
              <th className="px-3 py-1.5 text-center font-semibold text-gray-500">AI</th>
              <th className="px-3 py-1.5 text-center font-semibold text-gray-500">Duplicate</th>
              <th className="px-3 py-1.5 text-right font-semibold text-gray-500">Heatmap</th>
            </tr>
          </thead>
          <tbody>
            {pages.map(p => {
              const ai = p.ai_fraud_detail?.overall_assessment || (p.ai_fraud_indicators?.is_suspicious ? "suspicious" : "clean");
              const dup = p.is_duplicate_page ? `of P${p.duplicate_of}` : p.cross_claim_match ? "cross-claim" : "";
              return (
                <tr key={p.page_number} className={`border-b border-gray-100 ${sus.has(p.page_number) ? "bg-red-50" : ""}`} data-testid={`forensic-row-${p.page_number}`}>
                  <td className="px-3 py-1.5 font-mono font-medium">
                    <button onClick={() => onSelectPage?.(p.page_number)} className="hover:text-[#002FA7] hover:underline">P{p.page_number}</button>
                  </td>
                  <td className="px-3 py-1.5 capitalize text-gray-600">{(p.classification || "").replace(/_/g, " ")}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={p.ela?.hot_blocks > 0 ? "text-orange-700 font-bold" : "text-gray-600"}>{p.ela?.hot_blocks ?? "—"}</span>
                  </td>
                  <td className="px-3 py-1.5 text-center"><span className={(p.copy_move?.matched_pairs >= cmMin && (p.copy_move?.area_frac ?? 0) <= 0.15) ? "text-red-700 font-bold" : "text-gray-600"}>{p.copy_move?.matched_pairs ?? "—"}</span></td>
                  <td className="px-3 py-1.5 text-center"><span className={p.noise?.outlier_blocks > 0 ? "text-orange-700 font-bold" : "text-gray-600"}>{p.noise?.outlier_blocks ?? 0}</span></td>
                  <td className="px-3 py-1.5 text-center"><span className={p.sharpness?.hot_blocks > 0 ? "text-orange-700 font-bold" : "text-gray-600"}>{p.sharpness?.hot_blocks ?? 0}</span></td>
                  <td className="px-3 py-1.5 text-center">
                    <Badge variant="outline" className={`text-[9px] ${ai === "tampered" ? "bg-red-600 text-white border-0" : ai === "suspicious" ? "bg-orange-100 text-orange-800 border-orange-300" : "bg-green-50 text-green-700 border-green-200"}`}>
                      {ai.toUpperCase()}
                    </Badge>
                  </td>
                  <td className="px-3 py-1.5 text-center text-gray-600">
                    {dup && <span className="inline-flex items-center gap-1 text-red-700 font-medium"><Copy size={11} />{dup}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {p.tamper_heatmap_path && (
                      <button onClick={() => setHeatPage(p)} className="text-[#002FA7] hover:underline inline-flex items-center gap-1" data-testid={`heatmap-btn-${p.page_number}`}>
                        <Fire size={12} /> View
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <HeatmapDialog claimId={claimId} page={heatPage} onClose={() => setHeatPage(null)} />
    </div>
  );
}
